import { formatDate, parseDate } from "@progress/kendo-intl";

const FormatShortDate = "d"; // M/d/y
const FormatGeneral = "g"; // M/d/y h:mm a
const FormatUTC = "u";
const FormatDateTime = "MM/dd/yyyy h:mm:ss a";
const FormatYMD = "yyyy-MM-dd";
const FormatMD = "MMM dd";
const FormatMY = "MMM yyyy";
const FormatM = "MMM";

export default class DateUtility {
  static formatShortDate = (date: string | Date): string => this.format(date, FormatShortDate);

  static formatDateGeneral = (date: string | Date): string => this.format(date, FormatGeneral);

  static formatUTC = (date: string | Date): string => this.format(date, FormatUTC);

  static formatDateTime = (date: string | Date): string => this.format(date, FormatDateTime);

  static formatDateMD = (date: string | Date): string => this.format(date, FormatMD);

  static formatDateMY = (date: string | Date): string => this.format(date, FormatMY);

  static formatDateM = (date: string | Date): string => this.format(date, FormatM);

  static formatDateYMD = (date: string | Date): string => this.format(date, FormatYMD);

  static dateAsUtc = (date: Date): Date =>
    new Date(
      Date.UTC(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
        date.getHours(),
        date.getMinutes(),
        date.getSeconds(),
      ),
    );

  static parseMonth(dateString: string) {
    const date = parseDate(dateString);

    date.setDate(1);

    return date;
  }

  static addOffsetToDateString(dateString: string, offset: number) {
    const date = parseDate(dateString);
    const newDate = this.addMonthOffset(date, offset);
    const newDateString = formatDate(newDate, FormatYMD);
    return newDateString;
  }

  static convertToDateWithOffset(dateString: string, offset: number) {
    const date = parseDate(dateString);
    const newDate = this.addMonthOffset(date, offset);
    return newDate;
  }

  static addMonthOffset(date: Date, offset: number) {
    const clone = new Date(date);
    clone.setMonth(date.getMonth() + offset);
    return clone;
  }

  static isDateInArray = (dates: Date[], date1: Date): boolean => {
    return dates.some((date2: Date) => this.isEqualDate(date2, date1));
  };

  private static isEqualDate = (date1: Date, date2: Date): boolean => {
    const isEqual =
      date1.getDate() === date2.getDate() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getFullYear() === date2.getFullYear();

    return isEqual;
  };

  static isWeekend = (date: Date): boolean => {
    const day = date.getDay();
    return day === 0 || day === 6; // 0 = Sunday, 6 = Saturday
  };

  static countBusinessDaysPerMonth(
    startDate: Date,
    endDate: Date,
    holidays: Date[],
  ): [number[], number] {
    // Set startDate to the first day of the month
    startDate = new Date(startDate.getFullYear(), startDate.getMonth(), 1);

    // Set endDate to the last day of the month (day 0 of the next month = last day of prev month)
    endDate = new Date(endDate.getFullYear(), endDate.getMonth() + 1, 0);

    const businessDaysPerMonth: number[] = [];

    let currentDate = new Date(startDate.getTime());
    let currentMonth = currentDate.getMonth();
    let currentMonthCount = 0;
    let total = 0;

    while (currentDate <= endDate) {
      if (currentDate.getMonth() !== currentMonth) {
        businessDaysPerMonth.push(currentMonthCount);
        currentMonth = currentDate.getMonth();
        currentMonthCount = 0;
      }

      if (
        !DateUtility.isWeekend(currentDate) &&
        !DateUtility.isDateInArray(holidays, currentDate)
      ) {
        currentMonthCount++;
        total++;
      }

      currentDate.setDate(currentDate.getDate() + 1);
    }

    // Push the count for the last month
    businessDaysPerMonth.push(currentMonthCount);

    return [businessDaysPerMonth, total];
  }

  static format = (date: string | Date, format: string): string => {
    try {
      const newDate = typeof date === "string" ? parseDate(date) : date;

      return date
        ? formatDate(format === FormatUTC ? this.dateAsUtc(newDate) : newDate, format)
        : "";
    } catch {
      return "";
    }
  };

  /**
   * Returns the next business day after the given date, skipping weekends and holidays.
   * @param date The date to start from (exclusive)
   * @param holidays Array of holiday strings in yyyy-MM-dd format
   */
  static getNextBusinessDay(date: Date, holidays: string[]): Date {
    let next = new Date(date);
    next.setDate(next.getDate() + 1);
    while (DateUtility.isWeekend(next) || holidays.includes(DateUtility.formatDateYMD(next))) {
      next.setDate(next.getDate() + 1);
    }
    return next;
  }
}
